import { App as CapApp } from '@capacitor/app';

let cachedVersion = null;
let versionFetchPromise = null;

/**
 * Get the app version dynamically from Capacitor
 * Always fetches fresh from Capacitor to ensure we get the updated version after app updates
 * Uses memory cache to avoid multiple Capacitor calls
 * @returns {Promise<string>} App version string
 */
export async function getAppVersion() {
  // If we already have a fetch in progress, wait for it
  if (versionFetchPromise) {
    return versionFetchPromise;
  }

  versionFetchPromise = (async () => {
    try {
      // Always fetch fresh from Capacitor to get the current app version
      // This ensures we get the updated version after app updates
      const info = await CapApp.getInfo();
      if (info && info.version) {
        const newVersion = info.version;

        // Update memory cache (always update since we're fetching fresh)
        cachedVersion = newVersion;

        return newVersion;
      }
    } catch (error) {
      console.warn('[App Version] Failed to get version from Capacitor:', error);
      // Fall through to use memory cache or fallback
    }

    // If Capacitor fetch failed but we have memory cache, use it
    if (cachedVersion) {
      return cachedVersion;
    }

    // Final fallback - always return 1.0.0 if all else fails
    return '1.0.0';
  })();

  try {
    const result = await versionFetchPromise;
    versionFetchPromise = null; // Clear promise after completion
    return result;
  } catch (error) {
    console.error('[App Version] Unexpected error getting version:', error);
    versionFetchPromise = null;
    return '1.0.0'; // Always return fallback on any error
  }
}

/**
 * Get the app version synchronously (uses memory cache only)
 * NOTE: This uses memory cache and may return '1.0.0' if called before getAppVersion() completes
 * Always prefer getAppVersion() when possible, especially on app startup
 * @returns {string} App version string (always returns '1.0.0' if not cached yet)
 */
export function getAppVersionSync() {
  // Use in-memory cache if available (set by getAppVersion())
  if (cachedVersion) {
    return cachedVersion;
  }

  // Return fallback if not cached yet (getAppVersion() hasn't completed)
  return '1.0.0';
}
